import "@/elements/main";

interface Attributes {
  widgetId: string;
  propertyId: string | undefined;
  shouldAutoInjectBookingButton: boolean;
}

const WIDGET_ID_DATA_ATTRIBUTE = "data-widget-id";
const PROPERTY_ID_DATA_ATTRIBUTE = "data-property-id";
const SHOULD_AUTO_INJECT_BOOKING_BUTTON_DATA_ATTRIBUTE =
  "data-should-auto-inject-booking-button";

const findScriptElement = (): HTMLOrSVGScriptElement => {
  const scriptElement = document.currentScript;

  if (!scriptElement) {
    throw new Error("Document was not currently executing any script");
  }

  return scriptElement;
};

const extractAttributesFromScriptElement = (
  scriptElement: HTMLOrSVGScriptElement,
): Attributes => {
  const widgetId = scriptElement.getAttribute(WIDGET_ID_DATA_ATTRIBUTE);

  if (!widgetId) {
    throw new Error(
      `Script element contained no '${WIDGET_ID_DATA_ATTRIBUTE}' attribute, or it was empty`,
    );
  }

  const propertyId =
    scriptElement.getAttribute(PROPERTY_ID_DATA_ATTRIBUTE) ?? undefined;

  const shouldAutoInjectBookingButton =
    scriptElement.getAttribute(
      SHOULD_AUTO_INJECT_BOOKING_BUTTON_DATA_ATTRIBUTE,
    ) !== "false";

  return { widgetId, propertyId, shouldAutoInjectBookingButton };
};

/**
 * If the DOM is already ready, the elements will be immediately injected.
 * Otherwise, an event listener is registered to inject the elements when the
 * DOM is ready.
 */
const injectElements = (attributes: Attributes) => {
  if (/complete|interactive|loaded/.test(document.readyState)) {
    insertHTML(attributes);
  } else {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        insertHTML(attributes);
      },
      { once: true },
    );
  }
};

const insertHTML = ({
  widgetId,
  propertyId,
  shouldAutoInjectBookingButton,
}: Attributes) => {
  if (shouldAutoInjectBookingButton) {
    document.body.insertAdjacentHTML(
      "beforeend",
      `<bloom-booking-button ${propertyId ? `property-id=${propertyId}` : ""} was-auto-injected></bloom-booking-button>`,
    );
  }

  document.body.insertAdjacentHTML(
    "beforeend",
    `<bloom-booking-dialog widget-id=${widgetId}></bloom-booking-dialog>`,
  );
};

try {
  const scriptElement = findScriptElement();

  const attributes = extractAttributesFromScriptElement(scriptElement);

  injectElements(attributes);
} catch (error) {
  console.error(
    error instanceof Error
      ? `Could not render a Bloom widget due to the following error: ${error.message}.`
      : error,
  );
}
